import { apiFetch } from "../lib/api";

export interface WorkspaceListItem {
  id: string;
  accountId?: string;
  workspaceId?: string;
  actorId?: string;
  accessRole?: "admin" | "editor" | "viewer";
  name: string;
  artifact: WorkspaceArtifact;
  role: "owner" | "manager" | "member";
  previewUrl?: string;
  createdAt?: string;
  lastActivityAt?: string;
}

export type WorkspaceArtifact =
  | {
      kind: "website";
      status: "draft" | "live";
    }
  | {
      kind: "ios_app";
      status: "draft" | "testing" | "live";
    };

export type WorkspaceMessageStatus =
  | "processing"
  | "working"
  | "continuing"
  | "needs_more_credits"
  | "complete"
  | "failed";

export interface WorkspaceMessageAttachment {
  kind: "image";
  filename: string;
  contentType: string;
  size: number;
  url?: string;
}

export type WorkspaceTodoStatus = "pending" | "in_progress" | "completed";

export interface WorkspaceTodo {
  id: string;
  content: string;
  status: WorkspaceTodoStatus;
}

export interface WorkspaceMessage {
  messageId: string;
  text: string;
  reply?: string;
  status: WorkspaceMessageStatus;
  workId?: string;
  progress?: string;
  previewUrl?: string;
  attachments?: WorkspaceMessageAttachment[];
  todos?: WorkspaceTodo[];
  cloudJobBudgetCredits?: number;
  suggestedContinuationCredits?: number;
  continuationId?: string;
  continuationRetryAt?: string;
  createdAt: string;
  updatedAt: string;
}

export class WorkspaceApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "WorkspaceApiError";
  }
}

export async function fetchWorkspace(
  workspaceId: string,
): Promise<WorkspaceListItem> {
  const response = await workspaceRequest<{ workspace: WorkspaceListItem }>(
    `/workspaces/${encodeURIComponent(workspaceId)}`,
  );
  return response.workspace;
}

export async function fetchWorkspaceList(): Promise<WorkspaceListItem[]> {
  const response = await workspaceRequest<{ workspaces: WorkspaceListItem[] }>(
    "/workspaces",
  );
  return response.workspaces;
}

export async function createWorkspace(input: {
  businessName: string;
  slug: string;
  workspaceId: string;
}): Promise<WorkspaceListItem> {
  const response = await workspaceRequest<{ workspace: WorkspaceListItem }>(
    "/workspaces",
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return response.workspace;
}

export async function fetchWorkspaceMessages(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WorkspaceMessage[]> {
  const response = await withMessageRefreshDeadline(
    (refreshSignal) =>
      workspaceRequest<{ messages: WorkspaceMessage[] }>(
        `/workspaces/${encodeURIComponent(workspaceId)}/messages`,
        { signal: refreshSignal },
      ),
    signal,
  );
  return response.messages;
}

async function withMessageRefreshDeadline<T>(
  request: (signal: AbortSignal) => Promise<T>,
  callerSignal?: AbortSignal,
): Promise<T> {
  if (callerSignal?.aborted) throw callerSignal.reason;

  const controller = new AbortController();
  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  callerSignal?.addEventListener("abort", onCallerAbort, { once: true });
  let onAbort: () => void;
  const canceled = new Promise<never>((_, reject) => {
    onAbort = () => reject(controller.signal.reason);
    controller.signal.addEventListener("abort", onAbort, { once: true });
  });
  const deadline = setTimeout(() => {
    controller.abort(new Error("Refreshing messages took too long. Try again."));
  }, 45_000);

  try {
    // Race the entire request, including JSON parsing. A stalled refresh must
    // settle so the query can recover instead of deduplicating into it forever.
    return await Promise.race([request(controller.signal), canceled]);
  } finally {
    clearTimeout(deadline);
    callerSignal?.removeEventListener("abort", onCallerAbort);
    controller.signal.removeEventListener("abort", onAbort!);
  }
}

export async function sendWorkspaceMessage(
  workspaceId: string,
  input: {
    messageId: string;
    text: string;
    cloudJobBudgetCredits?: number;
    attachment?: File;
  },
): Promise<WorkspaceMessage> {
  let request: RequestInit;
  if (input.attachment) {
    const body = new FormData();
    body.set("messageId", input.messageId);
    body.set("text", input.text);
    if (input.cloudJobBudgetCredits !== undefined) {
      body.set(
        "cloudJobBudgetCredits",
        String(input.cloudJobBudgetCredits),
      );
    }
    body.set("attachment", input.attachment, input.attachment.name);
    request = { method: "POST", body };
  } else {
    request = {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        messageId: input.messageId,
        text: input.text,
        cloudJobBudgetCredits: input.cloudJobBudgetCredits,
      }),
    };
  }
  try {
    const response = await workspaceRequest<{ message: WorkspaceMessage }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/messages`,
      request,
    );
    return response.message;
  } catch (error) {
    // A long-running update can finish after the edge times out the POST.
    // Reconcile ambiguous server/network failures by the idempotency key so a
    // completed request is not shown as failed or resent.
    if (error instanceof WorkspaceApiError && error.status < 500) throw error;
    const persisted = await fetchWorkspaceMessages(workspaceId).catch(() => null);
    const accepted = persisted?.find(
      (message) => message.messageId === input.messageId,
    );
    if (accepted) return accepted;
    throw error;
  }
}

export async function continueWorkspaceRun(
  workspaceId: string,
  runId: string,
  input: {
    continuationId: string;
    cloudJobBudgetCredits: number;
    retry?: boolean;
  },
): Promise<WorkspaceMessage> {
  const request = (retry = input.retry ?? false) =>
    workspaceRequest<{ message: WorkspaceMessage }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/runs/${encodeURIComponent(runId)}/continuations`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...input, ...(retry ? { retry: true } : {}) }),
      },
    );
  try {
    const response = await request();
    return response.message;
  } catch (error) {
    if (error instanceof WorkspaceApiError && error.status < 500) throw error;
    const persisted = await fetchWorkspaceMessages(workspaceId).catch(() => null);
    const accepted = persisted?.find((message) => message.workId === runId);
    if (
      accepted?.status === "continuing" &&
      accepted.continuationId === input.continuationId &&
      !input.retry
    ) {
      try {
        return (await request(true)).message;
      } catch {
        return accepted;
      }
    }
    if (
      accepted &&
      ["working", "continuing", "complete"].includes(accepted.status)
    ) {
      return accepted;
    }
    throw error;
  }
}

async function workspaceRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await apiFetch(path, {
    ...init,
    credentials: "include",
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new WorkspaceApiError(
      response.status,
      body?.error ?? `Workspace request failed (${response.status})`,
    );
  }
  return (await response.json()) as T;
}
